/**
 * OpenRequestModal – skapere kan foreslå samarbeid uten kalender-slot.
 * Kan foreslå én dato, flere alternativer, eller be motpart sjekke kalenderen sin.
 */
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Send, Plus, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { t as T } from "i18next";

const TYPES = [
  { k: "sfs", label: "SFS" },
  { k: "mass", label: "MASS" },
  { k: "l4l", label: "L4L" },
  { k: "story_swap", label: "Story swap" },
  { k: "other", label: null },
];

const PLATFORMS = [
  { k: "onlyfans", label: "OnlyFans" },
  { k: "fansly", label: "Fansly" },
  { k: "instagram", label: "Instagram" },
  { k: "snapchat", label: "Snapchat" },
  { k: "tiktok", label: "TikTok" },
  { k: "x", label: "X / Twitter" },
  { k: "reddit", label: "Reddit" },
  { k: "bluesky", label: "Bluesky" },
  { k: "other", label: null },
];

export default function OpenRequestModal({ targetUsername, targetUserId, onClose, onSent }) {
  const [type, setType] = useState("sfs");
  const [typeOther, setTypeOther] = useState("");
  const [platform, setPlatform] = useState("onlyfans");
  const [mode, setMode] = useState("single"); // single | multi | ask
  const [singleDate, setSingleDate] = useState("");
  const [multiDates, setMultiDates] = useState([""]);
  const [message, setMessage] = useState("");
  const [dealOffer, setDealOffer] = useState("");
  const [busy, setBusy] = useState(false);

  const updateMulti = (i, v) => {
    const next = [...multiDates];
    next[i] = v;
    setMultiDates(next);
  };
  const addMulti = () => setMultiDates([...multiDates, ""]);
  const removeMulti = (i) => setMultiDates(multiDates.filter((_, idx) => idx !== i));

  const send = async () => {
    if (type === "other" && !typeOther.trim()) return toast.error(T("open_req.err_type"));
    let dates = [];
    if (mode === "single") {
      if (!singleDate) return toast.error(T("open_req.err_date"));
      dates = [singleDate];
    } else if (mode === "multi") {
      dates = multiDates.filter((d) => d);
      if (dates.length < 2) return toast.error(T("open_req.err_multi"));
    }
    setBusy(true);
    try {
      await api.post("/collab/open-requests", {
        target_username: targetUsername,
        target_user_id: targetUserId,
        type,
        type_other: type === "other" ? typeOther.trim() : null,
        platform,
        proposed_dates: dates,
        message: message.trim(),
        deal_offer: dealOffer.trim() || null,
      });
      toast.success(T("open_req.sent", { u: targetUsername }));
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("open_req.err_send"));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/80 backdrop-blur-sm p-4 pt-20 pb-12 overflow-y-auto"
      onClick={onClose} data-testid="open-request-modal">
      <div className="max-w-md w-full panel-tight p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 text-[var(--cream-faint)] hover:text-[var(--magenta)]">
          <X className="h-4 w-4" />
        </button>
        <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-1">{T("open_req.kicker")}</div>
        <h3 className="font-fraunces italic text-2xl text-[var(--cream)] mb-1">{T("open_req.to", { u: targetUsername })}</h3>
        <p className="text-xs text-[var(--cream-soft)] mb-5">
          {T("open_req.intro")}
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-1">{T("open_req.type")}</label>
              <select value={type} onChange={(e) => setType(e.target.value)}
                className="input-editorial w-full" data-testid="open-req-type">
                {TYPES.map((tp) => <option key={tp.k} value={tp.k}>{tp.label || T("open_req.other")}</option>)}
              </select>
            </div>
            <div>
              <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-1">{T("open_req.platform")}</label>
              <select value={platform} onChange={(e) => setPlatform(e.target.value)}
                className="input-editorial w-full" data-testid="open-req-platform">
                {PLATFORMS.map((p) => <option key={p.k} value={p.k}>{p.label || T("open_req.other")}</option>)}
              </select>
            </div>
          </div>
          {type === "other" && (
            <input value={typeOther} onChange={(e) => setTypeOther(e.target.value)} maxLength={60}
              placeholder={T("open_req.type_other_ph")}
              className="input-editorial w-full" data-testid="open-req-type-other" />
          )}

          {/* Dato-modus */}
          <div>
            <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-2">{T("open_req.date")}</label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {[
                { k: "single", label: T("open_req.mode_single") },
                { k: "multi", label: T("open_req.mode_multi") },
                { k: "ask", label: T("open_req.mode_ask") },
              ].map((m) => (
                <button key={m.k} type="button" onClick={() => setMode(m.k)}
                  className={`px-3 py-1.5 font-meta text-[10px] tracking-[0.15em] border ${
                    mode === m.k
                      ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10"
                      : "border-white/15 text-[var(--cream-soft)] hover:border-[var(--cream)]"
                  }`} data-testid={`open-req-mode-${m.k}`}>
                  {m.label}
                </button>
              ))}
            </div>
            {mode === "single" && (
              <input type="date" value={singleDate} onChange={(e) => setSingleDate(e.target.value)}
                className="input-editorial w-full" data-testid="open-req-single-date" />
            )}
            {mode === "multi" && (
              <div className="space-y-1.5">
                {multiDates.map((d, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input type="date" value={d} onChange={(e) => updateMulti(i, e.target.value)}
                      className="input-editorial flex-1" data-testid={`open-req-multi-${i}`} />
                    {multiDates.length > 1 && (
                      <button type="button" onClick={() => removeMulti(i)}
                        className="text-[var(--cream-faint)] hover:text-[var(--magenta)]">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addMulti}
                  className="font-meta text-[10px] text-[var(--magenta)] hover:underline inline-flex items-center gap-1">
                  <Plus className="h-3 w-3" /> {T("open_req.add_date")}
                </button>
              </div>
            )}
            {mode === "ask" && (
              <p className="text-xs text-[var(--cream-soft)] italic">
                {T("open_req.ask_hint")}
              </p>
            )}
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-1">{T("open_req.message")}</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={1000}
              placeholder={T("open_req.message_ph")}
              className="input-editorial w-full font-manrope text-sm" data-testid="open-req-message" />
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-1">{T("open_req.deal")}</label>
            <input value={dealOffer} onChange={(e) => setDealOffer(e.target.value)} maxLength={300}
              placeholder={T("open_req.deal_ph")}
              className="input-editorial w-full" data-testid="open-req-deal" />
          </div>
        </div>

        <button onClick={send} disabled={busy}
          className="btn-magenta w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="open-req-send">
          <Send className="h-4 w-4" /> {busy ? T("open_req.sending") : T("open_req.send")}
        </button>
      </div>
    </div>,
    document.body
  );
}
